// Tela de cadastro de usuário.
// Usa useJwtAuth para chamar signUp.

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { text } from '../text';
import { hooks } from '../hooks';
import { utils } from '../utils';
import { custom } from '../custom';
import { theme } from '../constants';
import { components } from '../components';

import { Mail, Key, User, Eye, EyeOff } from 'lucide-react';
import useJwtAuth from '../auth/services/jwt/useJwtAuth'; 

export const SignUp: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = hooks.useAppNavigate();
  const { signUp } = useJwtAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setTimeout(() => {
      window.scroll({ top: -1, left: 0, behavior: 'smooth' });
    }, 10);
  }, [pathname]);

  const isValidPassword = (password: string) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    return regex.test(password);
  };
  

  const handleSignUp = async () => {
    if (password !== confirmPassword) {
      setErrorMessage('As senhas não coincidem.');
      return;
    }

    if (!isValidPassword(password)) {
      setErrorMessage(
        'A senha deve ter pelo menos 8 caracteres, incluindo letras maiúsculas, minúsculas, números e caracteres especiais.'
      );
      return;
    }

    try {
      setErrorMessage(null);
      await signUp(email, password, firstName, lastName);
      navigate('/SignIn');
      //navigate('/VerifyYourPhoneNumber'); Fluxo anterior
    } catch (error) {
      setErrorMessage('Erro ao criar conta. Tente novamente.');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };


  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const renderHeader = (): JSX.Element => {
    return <components.Header goBack={true} />;
  };

  const renderContent = (): JSX.Element => {
    return (
      <main
        className="container"
        style={{ marginTop: 52, paddingTop: '18%', paddingBottom: 20, zIndex: 1 }}
      >
        <text.H1 style={{ marginBottom: 30 }}>Cadastre-se!</text.H1>

        {/* Primeiro Nome */}
        <custom.InputField
          leftIcon={
            <div
              style={{
                padding: 8,
                backgroundColor: theme.colors.lightGrey,
                borderRadius: '20%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <User size={18} color={theme.colors.mainDark} />
            </div>
          }
          containerStyle={{ marginBottom: 10 }}
          placeholder="Primeiro Nome"
          value={firstName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
        />

        {/* Último Nome */}
        <custom.InputField
          leftIcon={
            <div
              style={{
                padding: 8,
                backgroundColor: theme.colors.lightGrey,
                borderRadius: '20%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <User size={18} color={theme.colors.mainDark} />
            </div>
          }
          containerStyle={{ marginBottom: 10 }}
          placeholder="Último Nome"
          value={lastName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
        />


        {/* Email */}
        <custom.InputField
          leftIcon={
            <div
              style={{
                padding: 8,
                backgroundColor: theme.colors.lightGrey,
                borderRadius: '20%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Mail size={18} color={theme.colors.mainDark} />
            </div>
          }
          containerStyle={{ marginBottom: 10 }}
          placeholder="Digite seu email"
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
        />

        {/* Senha */}
        <custom.InputField
          leftIcon={
            <div
              style={{
                padding: 8,
                backgroundColor: theme.colors.lightGrey,
                borderRadius: '20%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Key size={18} color={theme.colors.mainDark} />
            </div>
          }
          rightIcon={
            <div onClick={togglePasswordVisibility} style={{ cursor: 'pointer' }}>
              {showPassword ? <Eye size={18} color={theme.colors.mainDark} /> : <EyeOff size={18} color={theme.colors.mainDark} />}
            </div>
          }
          containerStyle={{ marginBottom: 10 }}
          placeholder="Digite sua senha"
          value={password}
          type={showPassword ? 'text' : 'password'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
        />

        {/* Confirmar Senha */}
        <custom.InputField
          leftIcon={
            <div
              style={{
                padding: 8,
                backgroundColor: theme.colors.lightGrey,
                borderRadius: '20%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Key size={18} color={theme.colors.mainDark} />
            </div>
          }
          rightIcon={
            <div onClick={toggleConfirmPasswordVisibility} style={{ cursor: 'pointer' }}>
              {showConfirmPassword ? <Eye size={18} color={theme.colors.mainDark} /> : <EyeOff size={18} color={theme.colors.mainDark} />}
            </div>
          }
          containerStyle={{ marginBottom: 14 }}
          placeholder="Confirme sua senha"
          value={confirmPassword}
          type={showConfirmPassword ? 'text' : 'password'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
        />

        {/* Exibe erro caso ocorra */}
        {errorMessage && <p style={{ color: 'red', marginBottom: 10 }}>{errorMessage}</p>}

        {/* Botão de Cadastrar */}
        <components.Button
          title="Cadastrar"
          containerStyle={{ marginBottom: 14 }}
          onClick={handleSignUp}
        />

        <div style={{ ...utils.rowCenter(), marginBottom: 40 }}>
          <text.T16 style={{ marginRight: 4 }}>Já tem uma conta?</text.T16>
          <text.T16
            onClick={() => navigate('/SignIn')}
            style={{ cursor: 'pointer', color: theme.colors.mainColor }}
          >
            Entrar.
          </text.T16>
        </div>
      </main>
    );
  };

  return (
    <div id="screen">
      {renderHeader()}
      {renderContent()}
    </div>
  );
};
